import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { OnboardingAuthProvider } from "@/contexts/OnboardingAuthContext";
import { ErrorBoundary } from "@/components/Fallback";
import { GlobalSEOHead } from "@/components/GlobalSEOHead";
import ElidoHomePage from "@/pages/ElidoHomePage";
import NexusHomePage from "@/pages/NexusHomePage";
import GratisDemoPage from "@/pages/GratisDemoPage";
import GratisAnalysPage from "@/pages/GratisAnalysPage";
import DemoPage from "@/pages/DemoPage";
import AIDemoPage from "@/pages/AIDemoPage";
import AdminPage from "@/pages/AdminPage";
import LoginPage from "@/pages/LoginPage";
import VerifyPage from "@/pages/VerifyPage";
import CustomerPortal from "@/pages/CustomerPortal";
import PortalVerify from "@/pages/PortalVerify";
import SeoPage from "@/pages/SeoPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import ConfiguratorPage from "@/pages/ConfiguratorPage";
import IntegritetspolicyPage from "@/pages/IntegritetspolicyPage";
import CookiesPage from "@/pages/CookiesPage";
import AiChatbotHemsidaPage from "@/pages/AiChatbotHemsidaPage";
import ChatbotDemoPage from "@/pages/ChatbotDemoPage";
import NyHemsidaPage from "@/pages/NyHemsidaPage";
import OnboardingCreatePage from "@/pages/OnboardingCreatePage";
import OnboardingLoginPage from "@/pages/OnboardingLoginPage";
import OnboardingDraftPage from "@/pages/OnboardingDraftPage";
import NexusLayout from "@/components/NexusLayout";
import ScrollToTop from "@/components/ScrollToTop";
import PageviewTracker from "@/components/PageviewTracker";

// v3.48: preserves the :draftId param when redirecting the legacy
// /skapa-kundmottagare/utkast/:draftId URL to the new /skapa-ai-agent/...
function RedirectDraftToAiAgent() {
  const { draftId } = useParams();
  return <Navigate to={`/skapa-ai-agent/utkast/${draftId}`} replace />;
}

// SPA-level normalization for the legacy `/blogg` slug → canonical
// `/blog` (Swedish "blogg" is common in body copy but the route is
// English `/blog`). Preserves the optional :slug segment so bookmarks
// to /blogg/some-post redirect to /blog/some-post.
function RedirectBloggToBlog() {
  const { slug } = useParams();
  return <Navigate to={slug ? `/blog/${slug}` : '/blog'} replace />;
}

function App() {
  return (
    <ErrorBoundary 
      variant="sales" 
      onRetry={() => window.location.reload()}
    >
      <HelmetProvider>
        <AuthProvider>
          <OnboardingAuthProvider>
          <BrowserRouter>
          <ScrollToTop />
          <PageviewTracker />
          {/* GlobalSEOHead updates canonical/robots per route */}
          <GlobalSEOHead />
          <Routes>
            {/* Public onboarding flow — standalone (no NexusLayout wrapping) */}
            {/* v3.48: primary URL is /skapa-ai-agent. /skapa-kundmottagare is
                kept alive as a permanent redirect so old ads/SEO/inbound links
                don't 404. */}
            <Route path="/skapa-ai-agent" element={<OnboardingCreatePage />} />
            <Route path="/skapa-ai-agent/logga-in" element={<OnboardingLoginPage />} />
            <Route path="/skapa-ai-agent/utkast/:draftId" element={<OnboardingDraftPage />} />
            <Route path="/skapa-kundmottagare" element={<Navigate to="/skapa-ai-agent" replace />} />
            <Route path="/skapa-kundmottagare/logga-in" element={<Navigate to="/skapa-ai-agent/logga-in" replace />} />
            <Route path="/skapa-kundmottagare/utkast/:draftId" element={<RedirectDraftToAiAgent />} />

            {/* Elido-owned public routes — share the NexusLayout (header/footer/widget) */}
            <Route element={<NexusLayout />}>
              <Route path="/" element={<NexusHomePage />} />
              <Route path="/hemsidor" element={<ElidoHomePage />} />
              <Route path="/gratis-analys" element={<GratisAnalysPage />} />
              <Route path="/integritetspolicy" element={<IntegritetspolicyPage />} />
              <Route path="/cookies" element={<CookiesPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              {/* Legacy Swedish spelling → canonical /blog. Keeps old
                  bookmarks/links from 404'ing. Client-side redirect
                  only — production also handles this at the edge. */}
              <Route path="/blogg" element={<RedirectBloggToBlog />} />
              <Route path="/blogg/:slug" element={<RedirectBloggToBlog />} />
              {/* Dedicated landing page (Google Ads) — must come before /:slug catch-all */}
              <Route path="/ai-chatbot-hemsida" element={<AiChatbotHemsidaPage />} />
              <Route path="/ai-agent-hemsida" element={<Navigate to="/ai-chatbot-hemsida" replace />} />
              {/* Legacy /hemsidor-varianter → permanent redirect till /hemsidor.
                  Måste ligga före /:slug catch-all. */}
              <Route path="/hemsida-foretag" element={<Navigate to="/hemsidor" replace />} />
              <Route path="/ny-hemsida-foretag" element={<Navigate to="/hemsidor" replace />} />
              {/* Catch-all SEO landing pages — these are admin-driven via
                  /admin/seo-pages. Content lives in MongoDB and is fetched
                  by SeoPage on slug change. Slugs like /digital-kundmottagare,
                  /chatbot-ehandel, /vad-kostar-en-chatbot etc. are created
                  there. SeoPage resets state per slug and shows a clear
                  "Sidan hittades inte" if the API returns 404 (never a blank
                  page). */}
              <Route path="/:slug" element={<SeoPage />} />
            </Route>

            {/* Standalone routes — NO Elido header/footer/widget */}
            {/* /ny-hemsida — dedikerad Google Ads-landningssida (egen header/footer) */}
            <Route path="/ny-hemsida" element={<NyHemsidaPage />} />
            {/* /gratis-demo — dedikerad lead-gen-sida (egen avskalad header/footer) */}
            <Route path="/gratis-demo" element={<GratisDemoPage />} />
            {/* /demo and /d/:slug render the customer's own generated site */}
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/d/:slug" element={<AIDemoPage />} />
            {/* /demo/chatbot/:slug — public per-client chatbot demo (Elido-branded) */}
            <Route path="/demo/chatbot/:slug" element={<ChatbotDemoPage />} />
            <Route path="/start" element={<ConfiguratorPage />} />

            {/* Auth routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/verify" element={<VerifyPage />} />

            {/* Fas 3 — kundportal (separat från admin, egen auth-cookie) */}
            <Route path="/portal" element={<CustomerPortal />} />
            <Route path="/portal/verify" element={<PortalVerify />} />

            {/* Admin routes (protected) */}
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/demos" element={<AdminPage />} />
            <Route path="/admin/offers" element={<AdminPage />} />
            <Route path="/admin/website" element={<AdminPage />} />
            <Route path="/admin/leads" element={<AdminPage />} />
            <Route path="/admin/users" element={<AdminPage />} />
            <Route path="/admin/seo-pages" element={<AdminPage />} />
            <Route path="/admin/blog" element={<AdminPage />} />
            <Route path="/admin/media" element={<AdminPage />} />
            <Route path="/admin/homepage-images" element={<AdminPage />} />
            <Route path="/admin/clients" element={<AdminPage />} />
            <Route path="/admin/clients/:clientId" element={<AdminPage />} />
            <Route path="/admin/handymen" element={<AdminPage />} />
            <Route path="/admin/onboarding-drafts" element={<AdminPage />} />
            <Route path="/admin/google-kopplingar" element={<AdminPage />} />
            <Route path="/admin/optimering" element={<AdminPage />} />
            <Route path="/admin/chatgpt" element={<AdminPage />} />
            <Route path="/admin/chatgpt/consent" element={<AdminPage />} />
            <Route path="/admin/llm-diagnostik" element={<AdminPage />} />
            <Route path="/admin/blueprints" element={<AdminPage />} />
            <Route path="/admin/blueprints/:clientId" element={<AdminPage />} />
            <Route path="/admin/runtime-events" element={<AdminPage />} />
            <Route path="/admin/system/teknik" element={<AdminPage />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
        </OnboardingAuthProvider>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
